<template>
  <ion-page>
    <ion-header :translucent="true">
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-back-button default-href="/ai" />
        </ion-buttons>
        <ion-title>How This Works</ion-title>
      </ion-toolbar>
    </ion-header>

    <ion-content :fullscreen="true">

      <!-- Hero -->
      <section class="hero">
        <h1 class="hero-title">Opening Prep, Rebuilt From the Ground Up</h1>
        <p class="hero-tagline">Most opening tools tell you what grandmasters play. This one tells you what actually works — for someone exactly like you.</p>
      </section>

      <!-- The problem -->
      <section class="feature-section">
        <div class="section-header">
          <h2 class="section-title">Why Win/Loss Percentages Lie to You</h2>
        </div>
        <p class="section-intro">Every opening explorer shows you win percentages. The problem is that a great opening followed by a blunder on move 35 looks identical, in the data, to a bad opening that somehow survived. Win/loss percentages measure the whole game — not the opening.</p>
        <p class="section-intro">We measure something different: where the game actually stood at move 15. That's deep enough to capture what the opening produced, and early enough that it hasn't been overwritten by a random tactic, a time scramble, or an opponent's blunder twenty moves later. It's the fairest snapshot of whether an opening choice actually worked.</p>
      </section>

      <!-- Peer level -->
      <section class="feature-section">
        <div class="section-header">
          <div class="section-icon-wrap">
            <ion-icon :icon="peopleOutline" aria-hidden="true" />
          </div>
          <h2 class="section-title">Built For Your Rating, Not Magnus's</h2>
        </div>
        <p class="section-intro">A move that's theoretically perfect at the grandmaster level can be a trap at club level — if it requires precise follow-up that most players your rating won't find. So every analysis is filtered to games between players within 50 rating points of yours. You're not seeing how the opening performs in theory. You're seeing how it performs in games between people just like you.</p>
      </section>

      <!-- Frequency -->
      <section class="feature-section">
        <div class="section-header">
          <div class="section-icon-wrap">
            <ion-icon :icon="statsChartOutline" aria-hidden="true" />
          </div>
          <h2 class="section-title">What People Actually Play</h2>
        </div>
        <p class="section-intro">Next to every move in the Explorer is a frequency — how often that move is actually played at your rating level. This matters more than it looks. A rare trap that only works if your opponent falls for an obscure line isn't useful preparation; nobody you'll actually face plays it. The moves worth studying are the ones your real opponents will actually choose.</p>
      </section>

      <!-- The AI -->
      <section class="feature-section">
        <div class="section-header">
          <div class="section-icon-wrap">
            <ion-icon :icon="sparklesOutline" aria-hidden="true" />
          </div>
          <h2 class="section-title">A Coach, Reading the Data For You</h2>
        </div>
        <p class="section-intro">The Opening Explorer is free for all ChessVis users — see the outcome distributions, compare moves, and explore your openings as deeply as you like.</p>
        <p class="section-intro">Subscribers get one more thing: a coach who reads the data for you.</p>
        <p class="section-intro">Select any candidate moves and AI Opening Analysis will compare the full outcome distributions and tell you, in plain language, which moves are solid, which are sharp gambles, and which one the data actually recommends for a player at your level.</p>
        <p class="section-intro">The analysis is built strictly from the numbers — not from opening theory, not from what's "supposed" to be good. If the data contradicts the books, the AI will say so. That's the entire point.</p>
        <p class="section-intro">And to make it fun, choose from a range of Personas — a Washington Square Park chess hustler, a Kids Coach, Shakespeare, and others.</p>
        <p class="section-intro subscriber-callout">AI Opening Analysis is a ChessVis subscriber feature. One subscription unlocks this and everything else ChessVis has to offer.
            <a @click="onSubscriberClick()">One subscription</a> unlocks this and everything else ChessVis has to offer.</p>
      </section>

      <!-- How to use it -->
      <section class="feature-section">
        <div class="section-header">
          <h2 class="section-title">How To Use It</h2>
        </div>

        <div class="step-card">
          <div class="step-number">1</div>
          <div class="step-body">
            <h3>Browse the Explorer</h3>
            <p>Play through any opening. At each position, see every move that's actually been played at your rating, how often, and how those games turned out by move 15.</p>
          </div>
        </div>

        <div class="step-card">
          <div class="step-number">2</div>
          <div class="step-body">
            <h3>Select Your Candidates</h3>
            <p>Check the moves you're actually deciding between — the ones you already play, or are thinking of adding to your repertoire. The graph updates instantly, showing how real games played out at move 15 for each candidate. And if the graph feels like a lot to take in, that's exactly what the AI analysis is for — tap AI Compare and let it cut through the numbers and tell you what actually matters.</p>
          </div>
        </div>

        <div class="step-card">
          <div class="step-number">3</div>
          <div class="step-body">
            <h3>Get the Verdict</h3>
            <p>Tap AI Detail for one move, or AI Compare for several. The coach reads the distribution and tells you what it means — plainly, practically, and based only on what the data shows.</p>
          </div>
        </div>

        <div class="step-card">
          <div class="step-number">4</div>
          <div class="step-body">
            <h3>Save What Matters</h3>
            <p>Keep the analysis — board position, distribution chart, and commentary — as a reference you can return to.</p>
          </div>
        </div>
      </section>

      <!-- Credits note -->
      <section class="feature-section">
        <div class="feature-card subscription-card">
          <h3>About Credits</h3>
          <p>A detailed single-move analysis or a comparison of up to four moves uses 5 credits — the same pool used for full game analysis. Browsing the Explorer itself, including move frequencies and outcome percentages, is always free.</p>
        </div>
      </section>

      <!-- Final CTA -->
      <section class="final-cta-section">
        <h2 class="final-cta-title">Stop guessing. Start checking.</h2>
        <ion-button expand="block" class="final-cta-btn" @click="onExplorerClick">
          Open the Explorer
        </ion-button>
      </section>

    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonButtons, IonBackButton,
  IonContent, IonIcon, IonButton,
} from '@ionic/vue'
import {
  peopleOutline, statsChartOutline, sparklesOutline,
} from 'ionicons/icons'

const emit = defineEmits(['menu-item-click'])


function onExplorerClick() {
  emit("menu-item-click","ai_explorer","AI Opening Explorer","program")
}

function onSubscriberClick() {
    emit("menu-item-click","subscribe_blurb","Subscriber Benefits","program")
}

</script>

<style scoped>
ion-toolbar {
  --background: #0a0a1a;
  --color: #ffffff;
  --border-color: #1a1a3a;
}

ion-title {
  font-size: 17px;
  font-weight: 500;
}

ion-content {
  --background: #0a0a1a;
}

/* ── Hero ── */
.hero {
  padding: 32px 20px 28px;
  text-align: center;
  border-bottom: 0.5px solid #1a1a3a;
}

.hero-title {
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 12px;
  line-height: 1.3;
}

.hero-tagline {
  font-size: 15px;
  color: #a090e0;
  line-height: 1.6;
  margin: 0;
  font-style: italic;
}

/* ── Feature sections ── */
.feature-section {
  padding: 26px 16px 12px;
  border-bottom: 0.5px solid #1a1a3a;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.section-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #2a1a5a;
  color: #a090e0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.section-title {
  font-size: 19px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  line-height: 1.3;
}

.section-intro {
  font-size: 14px;
  line-height: 1.7;
  margin: 0 0 14px;
  color: #e8e8f0;
  opacity: 1;
}

/* ── Step cards ── */
.step-card {
  display: flex;
  gap: 14px;
  margin-bottom: 16px;
}

.step-number {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #2d2060;
  color: #CECBF6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
}

.step-body h3 {
  font-size: 15px;
  font-weight: 600;
  color: #e0e0f0;
  margin: 0 0 6px;
}

.step-body p {
  font-size: 13px;
  color: #b0b0bc;
  line-height: 1.7;
  margin: 0;
}

/* ── Subscription card ── */
.subscription-card {
  background: #121224;
  border-radius: 12px;
  padding: 16px;
  border: 0.5px solid #1a1a3a;
}

.subscription-card h3 {
  font-size: 15px;
  font-weight: 600;
  color: #a090e0;
  margin: 0 0 8px;
}

.subscription-card p {
  font-size: 13px;
  color: #b0b0bc;
  line-height: 1.7;
  margin: 0;
}

/* ── Final CTA ── */
.final-cta-section {
  padding: 32px 20px 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  text-align: center;
}

.final-cta-title {
  font-size: 22px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
}

.final-cta-btn {
  --background: #2d2060;
  --background-activated: #1f1645;
  --color: #ffffff;
  --border-radius: 14px;
  font-size: 16px;
  font-weight: 600;
  height: 54px;
  width: 100%;
}

.subscriber-callout {
  margin-top: 1.5rem;
  padding: 0.75rem 1rem;
  border-left: 3px solid #7F77DD;
  background-color: rgba(127, 119, 221, 0.08);
  border-radius: 0 4px 4px 0;
  font-style: italic;
}

.subscriber-callout a {
  color: #7F77DD;
  text-decoration: underline;
  font-weight: 600;
}

.subscriber-callout a:hover {
  color: #CECBF6;
}

</style>
